"use client";

import { Check } from "lucide-react";
import { cn } from "@/shared/core/utils";

interface StepProgressProps {
  currentStep: number;
  totalSteps: number;
  steps: string[];
  className?: string;
}

/**
 * Step progress indicator component
 * Replicates the old project's SignupProgress component
 */
export function StepProgress({
  currentStep,
  totalSteps,
  steps,
  className,
}: StepProgressProps) {
  return (
    <div className={cn("w-full", className)}>
      {/* Step indicators */}
      <div className="mb-4 flex items-center justify-center">
        {steps.map((step, index) => (
          <div key={index} className="flex items-center">
            {/* Step circle */}
            <div
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full border-2 transition-all duration-200",
                {
                  // Completed step
                  "border-primary-dark bg-primary-dark text-background":
                    index < currentStep,
                  // Current step
                  "border-primary-dark bg-background text-primary-dark":
                    index === currentStep,
                  // Future step
                  "border-muted-foreground/30 bg-background text-muted-foreground":
                    index > currentStep,
                }
              )}
            >
              {index < currentStep ? (
                <Check className="h-4 w-4" />
              ) : (
                <span className="text-sm font-medium">{index + 1}</span>
              )}
            </div>

            {/* Connector line */}
            {index < totalSteps - 1 && (
              <div
                className={cn(
                  "mx-2 h-0.5 w-8 transition-all duration-200 sm:mx-4 sm:w-12",
                  {
                    "bg-primary-dark": index < currentStep,
                    "bg-muted-foreground/30": index >= currentStep,
                  }
                )}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step label */}
      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Step {currentStep + 1} of {totalSteps}: {steps[currentStep]}
        </p>
      </div>
    </div>
  );
}

export default StepProgress;
